import { useMutation } from "@apollo/client/react";
import { useDebouncedCallback } from "@hooks/use-debounced-callback";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import React, { useState, useEffect } from "react";

import { AccountItemLine } from "./AccountItem";
import UPDATE_USER_FF_SEGMENT from "../../graphql/mutations/UpdateUserFfSegment";
import { toTitleCase } from "../../utils/index";
import { alert } from "../common/Alert";
import { DSToggle } from "../design-system/molecules";

const DEBOUNCE_TOGGLE_DELAY_MS = 300;

const FeatureToggle = ({
  title,
  description,
  flagKey,
  segmentKey,
  invertToggle = false,
}) => {
  const { [flagKey]: initialState } = useFlags();
  const [isEnabled, setIsEnabled] = useState(initialState);

  const [updateUserFfSegment, { loading }] = useMutation(
    UPDATE_USER_FF_SEGMENT,
  );

  useEffect(() => {
    setIsEnabled(initialState);
  }, [initialState]);

  const debouncedHandleToggle = useDebouncedCallback(async () => {
    if (loading) return;
    try {
      const action = invertToggle
        ? isEnabled
          ? "ADD"
          : "REMOVE"
        : isEnabled
          ? "REMOVE"
          : "ADD";
      const response = await updateUserFfSegment({
        variables: {
          action,
          segmentKey,
        },
      });
      if (!response.data.updateUserFfSegment.success) {
        alert(
          "error",
          "Failed to update setting. Please try again later or contact support.",
        );
        return;
      } else {
        alert("success", "Setting updated successfully.");
        mixpanel.track("Updated Account Setting", {
          settingName: toTitleCase(segmentKey),
          settingKey: segmentKey,
          settingValue: action,
        });
      }
      setIsEnabled(!isEnabled);
    } catch {
      alert(
        "error",
        "Failed to update setting. Please try again later or contact support.",
      );
    }
  }, DEBOUNCE_TOGGLE_DELAY_MS);

  return (
    <AccountItemLine dataTestId={`feature-toggle-${segmentKey}`}>
      <div className="text-secondary text-xs md:text-sm lg:w-96 mr-md">
        <strong className="text-primary">{title}: </strong>
        {description}
      </div>
      <div className="flex flex-row items-center gap-sm cursor-pointer">
        <DSToggle
          checked={invertToggle ? !isEnabled : isEnabled}
          onChange={debouncedHandleToggle}
          disabled={loading}
          dataTestId={`feature-toggle-${segmentKey}-switch`}
          aria-label={title}
        />
      </div>
    </AccountItemLine>
  );
};

export default FeatureToggle;
